import { api } from '../lib/api';

export type HomeCandidateResult =
  | { ok: true; data: any }
  | { ok: false; error: string; acceptanceRequired?: boolean };

const cache = new Map<string, any>();
const inflight = new Map<string, Promise<HomeCandidateResult>>();
let generation = 0;

function profileKey(email: string) {
  return email.trim().toLowerCase();
}

function normalizeCandidate(data: any) {
  return {
    ...data,
    education: Array.isArray(data?.education) ? data.education : [],
    experience: Array.isArray(data?.experience) ? data.experience : [],
  };
}

export function clearHomeCandidate(email?: string) {
  generation += 1;
  if (!email) {
    cache.clear();
    inflight.clear();
    return;
  }
  const key = profileKey(email);
  cache.delete(key);
  inflight.delete(key);
}

/** One slim profile fetch shared by the home page and the onboarding gate. */
export function loadHomeCandidate(email: string): Promise<HomeCandidateResult> {
  const key = profileKey(email);
  if (!key) return Promise.resolve({ ok: false, error: 'Missing email' });

  const cached = cache.get(key);
  if (cached) return Promise.resolve({ ok: true, data: cached });

  const pending = inflight.get(key);
  if (pending) return pending;

  const requestGeneration = generation;
  const request = api
    .get<any>(`/api/candidates/email/${encodeURIComponent(email.trim())}?view=home`)
    .then((res): HomeCandidateResult => {
      if (!res.ok) {
        return {
          ok: false,
          error: res.error,
          acceptanceRequired: Boolean(res.acceptanceRequired),
        };
      }
      const data = normalizeCandidate(res.data);
      if (requestGeneration === generation) cache.set(key, data);
      return { ok: true, data };
    })
    .finally(() => {
      if (inflight.get(key) === request) inflight.delete(key);
    });

  inflight.set(key, request);
  return request;
}
